import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Logo } from '@/components/Logo';
import Seo from '@/components/Seo';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';

/**
 * LegalDocumentPage
 * -----------------
 * Shared renderer for /privacy and /terms. Fetches `/api/public/legal/{type}`
 * (the backend is the source of truth so we never end up with the React
 * copy of the wording drifting from the audit log).
 *
 * The Markdown body is rendered with a tiny, intentionally-simple parser:
 * we only support the structures our policy actually uses (#/##/###
 * headings, > blockquotes, lists, **bold**, `code`, and paragraphs). This
 * keeps us from pulling in a Markdown dependency just for two pages.
 */
export function LegalDocumentPage({ docType }) {
    const { t: t42 } = useLocaleR42();
    const [doc, setDoc] = useState(null);
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState('');

    // SEO audit fix — unique per-document title/description/canonical
    // (these three routes previously fell back to the homepage meta).
    const SEO_BY_TYPE = {
        privacy: {
            title: 'Privacy Policy | SplashPad',
            description: 'How SplashPad collects, uses, and protects data for swim coaches, parents, and students — including data deletion rights.',
            canonical: 'https://splashpad.pro/privacy',
        },
        terms: {
            title: 'Terms of Service | SplashPad',
            description: 'The terms that govern using SplashPad — the swim lesson registration platform for independent swim coaches and schools.',
            canonical: 'https://splashpad.pro/terms',
        },
        sms: {
            title: 'SMS Terms & Consent | SplashPad',
            description: 'SplashPad SMS program disclosure: message types, frequency, opt-out (STOP), help (HELP), and carrier terms.',
            canonical: 'https://splashpad.pro/sms-terms',
        },
        accessibility: {
            title: 'Accessibility Statement | SplashPad',
            description: 'SplashPad\'s commitment to WCAG 2.1 AA accessibility for coaches, parents, and visitors — what we do, known limitations, and how to reach us.',
            canonical: 'https://splashpad.pro/accessibility',
        },
    };
    const seo = SEO_BY_TYPE[docType] || SEO_BY_TYPE.privacy;

    useEffect(() => {
        let cancelled = false;
        api.get(`/public/legal/${docType}`)
            .then((r) => {
                if (cancelled) return;
                setDoc(r.data.document);
                setMeta({ version: r.data.version, effective_date: r.data.effective_date });
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.response?.data?.detail || 'Could not load that document right now.');
            });
        return () => { cancelled = true; };
    }, [docType]);

    return (
        <div className="min-h-screen bg-[#f4f7fb]">
            <Seo title={seo.title} description={seo.description} canonical={seo.canonical} />
            <div className="flex items-center justify-between px-4 py-4 sm:px-6">
                <Link to="/" data-testid="legal-back-home-link"><Logo /></Link>
                <Button asChild variant="outline" className="h-9 rounded-xl" data-testid="legal-back-button">
                    <Link to="/"><ArrowLeft className="mr-1 h-3.5 w-3.5" /> {t42("Home")}</Link>
                </Button>
            </div>
            <div className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
                <Card className="sd-card p-6 sm:p-10" data-testid={`legal-${docType}-card`}>
                    {error ? (
                        <div className="flex items-center gap-2 rounded-xl border border-[#ff6b35]/40 bg-[#fff6f0] p-4 text-sm text-[#ff6b35]" data-testid="legal-error">
                            <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
                        </div>
                    ) : !doc ? (
                        <div className="py-12 text-center text-sm text-[#1a3a5c]/70" data-testid="legal-loading">
                            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> {t42("Loading…")} </div>
                    ) : (
                        <article className="legal-doc text-[#0a1628]" data-testid={`legal-${docType}-content`}>
                            <RenderMarkdown body={doc.body_md} />
                        </article>
                    )}
                    {meta && (
                        <div className="mt-8 border-t border-[#0a1628]/10 pt-4 text-xs text-[#1a3a5c]/70" data-testid="legal-meta"> {t42("Document version")} <strong>{meta.version}</strong> {t42("· effective")} {meta.effective_date}
                        </div>
                    )}
                </Card>
                <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs text-[#1a3a5c]/70">
                    <Link to="/privacy" className="hover:underline" data-testid="legal-footer-privacy-link">{t42("Privacy Policy")}</Link>
                    <span aria-hidden>·</span>
                    <Link to="/terms" className="hover:underline" data-testid="legal-footer-terms-link">{t42("Terms of Service")}</Link>
                    <span aria-hidden>·</span>
                    <Link to="/accessibility" className="hover:underline" data-testid="legal-footer-accessibility-link">{t42("Accessibility")}</Link>
                    <span aria-hidden>·</span>
                    <Link to="/privacy/delete-my-data" className="hover:underline" data-testid="legal-footer-deletion-link">{t42("Your Privacy Choices")}</Link>
                </div>
            </div>
        </div>
    );
}

export default LegalDocumentPage;

/**
 * Tiny purpose-built Markdown renderer. We deliberately do not support
 * arbitrary HTML — the policy body comes from our trusted backend file
 * but we still escape angle brackets before injecting, then re-apply the
 * structural transforms we care about.
 */
function RenderMarkdown({ body }) {
    if (!body) return null;
    const blocks = parseBlocks(body);
    return (
        <>
            {blocks.map((b, i) => renderBlock(b, i))}
        </>
    );
}

function parseBlocks(text) {
    const lines = text.split(/\r?\n/);
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) { i += 1; continue; }
        // Headings
        const h = line.match(/^(#{1,6})\s+(.*)$/);
        if (h) {
            blocks.push({ type: 'heading', level: h[1].length, text: h[2] });
            i += 1; continue;
        }
        // Horizontal rule
        if (line.trim() === '---') { blocks.push({ type: 'hr' }); i += 1; continue; }
        // Blockquote (one or more consecutive `> ` lines)
        if (line.startsWith('> ')) {
            const buf = [];
            while (i < lines.length && lines[i].startsWith('> ')) {
                buf.push(lines[i].slice(2));
                i += 1;
            }
            blocks.push({ type: 'quote', text: buf.join('\n') });
            continue;
        }
        // Bullet list — a bullet line plus any indented continuation lines
        // (our source markdown is hard-wrapped at ~72 cols).
        if (/^\s*[*-]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[*-]\s+/.test(lines[i])) {
                const m = lines[i].match(/^(\s*)[*-]\s+(.*)$/);
                const item = { indent: m[1].length, text: m[2] };
                i += 1;
                while (i < lines.length && /^\s+\S/.test(lines[i]) && !/^\s*[*-]\s+/.test(lines[i])) {
                    item.text += ` ${lines[i].trim()}`;
                    i += 1;
                }
                items.push(item);
            }
            blocks.push({ type: 'list', items });
            continue;
        }
        // Paragraph — join consecutive non-empty lines
        const buf = [line];
        i += 1;
        while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|>\s|[*-]\s|---$)/.test(lines[i])) {
            buf.push(lines[i]);
            i += 1;
        }
        blocks.push({ type: 'p', text: buf.join(' ') });
    }
    return blocks;
}

function renderBlock(b, key) {
    switch (b.type) {
        case 'heading': {
            const Tag = `h${b.level}`;
            const cls = b.level === 1
                ? 'mt-0 mb-3 font-display text-3xl font-bold text-[#0a1628]'
                : b.level === 2
                    ? 'mt-6 mb-2 font-display text-xl font-bold text-[#0a1628]'
                    : 'mt-4 mb-2 font-display text-base font-bold text-[#0a1628]';
            return <Tag key={key} className={cls}>{renderInline(b.text)}</Tag>;
        }
        case 'hr':
            return <hr key={key} className="my-6 border-t border-[#0a1628]/10" />;
        case 'quote':
            return (
                <blockquote key={key} className="my-4 rounded-xl border-l-4 border-[#00d4ff] bg-[#00d4ff]/8 px-4 py-3 text-sm text-[#0a1628]">
                    {b.text.split('\n').map((ln, j) => (
                        // Line index within a quote block IS the identity — the
                        // text is regenerated per render from server content.
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={`q-${key}-${j}`}>{renderInline(ln)}</div>
                    ))}
                </blockquote>
            );
        case 'list':
            return (
                <ul key={key} className="my-3 list-disc space-y-1 pl-6 text-sm text-[#0a1628]">
                    {b.items.map((it, j) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={`li-${key}-${j}`} className={it.indent >= 2 ? 'ml-5 list-[circle]' : undefined}>{renderInline(it.text)}</li>
                    ))}
                </ul>
            );
        case 'p':
        default:
            return <p key={key} className="my-3 text-sm leading-6 text-[#0a1628]">{renderInline(b.text)}</p>;
    }
}

/** Inline emphasis: **bold**, `code`, and bare URLs. Returns an array of
 *  React nodes / strings safe to render. */
function renderInline(text) {
    if (!text) return null;
    // Pattern matches **bold**, `code`, or http(s) URLs.
    const re = /(\*\*[^*]+\*\*|`[^`]+`|https?:\/\/[^\s)]+)/g;
    const out = [];
    let last = 0;
    let match;
    let idx = 0;
    // eslint-disable-next-line no-cond-assign
    while ((match = re.exec(text)) !== null) {
        if (match.index > last) {
            out.push(text.slice(last, match.index));
        }
        const token = match[0];
        if (token.startsWith('**') && token.endsWith('**')) {
            out.push(<strong key={idx}>{token.slice(2, -2)}</strong>);
        } else if (token.startsWith('`') && token.endsWith('`')) {
            out.push(<code key={idx} className="rounded bg-[#f4f7fb] px-1 py-0.5 font-mono text-xs text-[#0a1628]">{token.slice(1, -1)}</code>);
        } else if (/^https?:\/\//.test(token)) {
            // SEO audit fix: a URL at the end of a sentence ("…at
            // https://www.adr.org. If the AAA…") used to swallow the trailing
            // period into the href, producing a broken outbound link
            // (www.adr.org.). Peel trailing punctuation off the URL and render
            // it as plain text after the anchor.
            const trail = token.match(/[.,;:!?'")\]]+$/);
            const href = trail ? token.slice(0, -trail[0].length) : token;
            out.push(
                <a key={idx} href={href} target="_blank" rel="noopener noreferrer" className="text-[#00d4ff] underline hover:text-[#33ddff]">
                    {href}
                </a>
            );
            if (trail) out.push(trail[0]);
        } else {
            out.push(token);
        }
        idx += 1;
        last = re.lastIndex;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}
